import type { User, UserList } from "@eai-toolkit/client";
import { MeApi, ResourceAccessRequestsApi, UserApi } from "@eai-toolkit/client";
import { filtersToQ } from "@store/utils/queryUtils";
import { constructOrderParameter } from "@store/utils/sortUtils";

import { hydrateUsersQueryStateFromString } from "../queryState";
import { baseApi, handleQueryError } from "./baseApi";
import {
  axiosInstance,
  fieldSortComparator,
  getAllResources,
  getSingleResource,
} from "./utils";

const userApi = new UserApi(undefined, undefined, axiosInstance);
const meApi = new MeApi(undefined, undefined, axiosInstance);
const resourceAccessRequestsApi = new ResourceAccessRequestsApi(
  undefined,
  undefined,
  axiosInstance,
);

export const usersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query<User[], void>({
      queryFn: async () => {
        try {
          const { items } = await getAllResources<User>((params) =>
            userApi.v1ClusterUserGet(params),
          );
          return {
            data: items.sort(fieldSortComparator("fullName")) as User[],
          };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "User" as const, id })),
              { type: "User", id: "LIST" },
            ]
          : [{ type: "User", id: "LIST" }],
    }),

    searchUsers: builder.query<UserList, { queryKey: string }>({
      queryFn: async ({ queryKey }) => {
        try {
          const queryState = hydrateUsersQueryStateFromString(queryKey);
          const {
            paging: { offset, limit },
            query: { filters, sort },
          } = queryState;

          const order = constructOrderParameter(sort.fieldName, sort.direction);

          const response = await userApi.v1ClusterUserGet({
            offset,
            limit,
            q: filtersToQ(filters),
            order,
          });

          return { data: response.data };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...(result.items ?? []).map(({ id }) => ({
                type: "User" as const,
                id,
              })),
              { type: "User", id: "SEARCH_LIST" },
            ]
          : [{ type: "User", id: "SEARCH_LIST" }],
    }),

    getUserById: builder.query<User, { id: string }>({
      queryFn: async ({ id: userId }) => {
        try {
          const result = await getSingleResource(() =>
            userApi.v1UserGetById({ userId }),
          );
          if (result.error) {
            return {
              error: { status: "CUSTOM_ERROR", error: result.error },
            };
          }
          return { data: result.data };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (_result, _error, { id }) => [{ type: "User", id }],
    }),

    /**
     * Returns distinct users who have submitted access requests where the current
     * user is an approver. Falls back to the full user list on error.
     */
    getAccessRequestRequestors: builder.query<User[], void>({
      queryFn: async () => {
        try {
          const { items } = await getAllResources<User>((params) =>
            resourceAccessRequestsApi.v1ResourceAccessRequestorsList(params),
          );
          return { data: items.sort(fieldSortComparator("fullName")) };
        } catch {
          // Fallback: fetch all users
          try {
            const { items } = await getAllResources<User>((params) =>
              userApi.v1ClusterUserGet(params),
            );
            return { data: items.sort(fieldSortComparator("fullName")) };
          } catch (fallbackError) {
            return handleQueryError(fallbackError);
          }
        }
      },
      providesTags: [{ type: "User", id: "ACCESS_REQUEST_REQUESTORS" }],
    }),

    getMe: builder.query<User, void>({
      queryFn: async () => {
        try {
          const { data } = await meApi.v1MeGet();
          return { data: data as User };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: [{ type: "User", id: "ME" }],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useSearchUsersQuery,
  useGetUserByIdQuery,
  useGetMeQuery,
  useGetAccessRequestRequestorsQuery,
} = usersApi;
